import React from 'react';
import { useTranslation } from 'react-i18next';
import * as Popover from '@radix-ui/react-popover';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Search, X, ChevronDown } from 'lucide-react';
import { Member, OrganizationalRole } from '../../types';

interface PeopleFiltersProps {
  searchQuery: string;
  roleFilter: string;
  reportsToFilter: string;
  clientsFilter: string[];
  onSearchChange: (value: string) => void;
  onRoleChange: (value: string) => void;
  onReportsToChange: (value: string) => void;
  onClientsChange: (value: string[]) => void;
  onClearFilters: () => void;
  people: Member[];
  organizationalRoles: OrganizationalRole[];
  allClientTags: string[];
  filteredCount: number;
  totalCount: number;
}

export const PeopleFilters: React.FC<PeopleFiltersProps> = ({
  searchQuery,
  roleFilter,
  reportsToFilter,
  clientsFilter,
  onSearchChange,
  onRoleChange,
  onReportsToChange,
  onClientsChange,
  onClearFilters,
  people,
  organizationalRoles,
  allClientTags,
  filteredCount,
  totalCount,
}) => {
  const { t } = useTranslation('people');

  const hasFilters = searchQuery !== '' || roleFilter !== '__all__' || reportsToFilter !== '__all__' || clientsFilter.length > 0;

  const handleClientToggle = (client: string) => {
    if (clientsFilter.includes(client)) {
      onClientsChange(clientsFilter.filter(c => c !== client));
    } else {
      onClientsChange([...clientsFilter, client]);
    }
  };

  const handleRemoveClient = (client: string) => {
    onClientsChange(clientsFilter.filter(c => c !== client));
  };

  return (
    <div className="space-y-4 mb-6" data-testid="people-filters">
      <div className="flex flex-wrap items-end gap-4">
        <div className="flex-1 min-w-[250px]">
          <Label htmlFor="search-filter">{t('filters.search.label')}</Label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              id="search-filter"
              type="text"
              placeholder={t('filters.search.placeholder')}
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              className="pl-9"
              data-testid="people-filter-search"
            />
          </div>
        </div>

        <div className="flex-1 min-w-[200px]">
          <Label htmlFor="role-filter">{t('filters.role.label')}</Label>
          <Select value={roleFilter} onValueChange={onRoleChange}>
            <SelectTrigger id="role-filter" data-testid="people-filter-role">
              <SelectValue placeholder={t('filters.role.all')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__" data-testid="people-filter-role-all">{t('filters.role.all')}</SelectItem>
              {organizationalRoles
                .sort((a, b) => a.displayOrder - b.displayOrder)
                .map((role) => (
                  <SelectItem key={role.id} value={role.name} data-testid={`people-filter-role-${role.name}`}>
                    {role.name} {role.isAdmin && `(${t('form.admin')})`}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex-1 min-w-[200px]">
          <Label htmlFor="reports-to-filter">{t('filters.reportsTo.label')}</Label>
          <Select value={reportsToFilter} onValueChange={onReportsToChange}>
            <SelectTrigger id="reports-to-filter" data-testid="people-filter-reports-to">
              <SelectValue placeholder={t('filters.reportsTo.all')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__" data-testid="people-filter-reports-to-all">{t('filters.reportsTo.all')}</SelectItem>
              <SelectItem value="__none__" data-testid="people-filter-reports-to-none">{t('filters.reportsTo.noManager')}</SelectItem>
              {(() => {
                const managersWithReports = new Set(
                  people
                    .filter(p => p.reportsTo)
                    .map(p => p.reportsTo?.id)
                );
                return people
                  .filter(p => p.isActive !== false && managersWithReports.has(p.id))
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((person) => (
                    <SelectItem key={person.id} value={person.id} data-testid={`people-filter-reports-to-${person.id}`}>
                      {person.name}
                    </SelectItem>
                  ));
              })()}
            </SelectContent>
          </Select>
        </div>

        <div className="flex-1 min-w-[200px]">
          <Label htmlFor="clients-filter">{t('filters.clients.label')}</Label>
          <Popover.Root>
            <Popover.Trigger asChild>
              <button
                id="clients-filter"
                data-testid="people-filter-clients"
                className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <span className={clientsFilter.length === 0 ? 'text-muted-foreground' : ''}>
                  {clientsFilter.length === 0 
                    ? t('filters.clients.all') 
                    : t('filters.clients.selected', { count: clientsFilter.length })}
                </span>
                <ChevronDown className="h-4 w-4 opacity-50" />
              </button>
            </Popover.Trigger>
            <Popover.Portal>
              <Popover.Content
                className="z-50 w-[200px] rounded-md border bg-popover p-2 text-popover-foreground shadow-md outline-none animate-in fade-in-0 zoom-in-95"
                sideOffset={4}
                align="start"
              >
                <div className="max-h-[300px] overflow-y-auto">
                  {allClientTags.length === 0 ? (
                    <div className="py-2 px-2 text-sm text-muted-foreground">
                      {t('filters.clients.noClients')}
                    </div>
                  ) : (
                    allClientTags.sort().map((client) => (
                      <label
                        key={client}
                        className="flex items-center gap-2 py-1.5 px-2 rounded-sm hover:bg-accent cursor-pointer"
                        data-testid={`people-filter-client-${client}`}
                      >
                        <Checkbox
                          checked={clientsFilter.includes(client)}
                          onCheckedChange={() => handleClientToggle(client)}
                        />
                        <span className="text-sm">{client}</span>
                      </label>
                    ))
                  )}
                </div>
                {clientsFilter.length > 0 && (
                  <div className="border-t mt-2 pt-2">
                    <button
                      type="button"
                      onClick={() => onClientsChange([])}
                      className="w-full text-sm text-muted-foreground hover:text-foreground py-1"
                      data-testid="people-filter-clients-clear"
                    >
                      {t('filters.clients.clearAll')}
                    </button>
                  </div>
                )}
              </Popover.Content>
            </Popover.Portal>
          </Popover.Root>
        </div>

        {hasFilters && (
          <Button variant="outline" onClick={onClearFilters} data-testid="people-filter-clear">
            <X className="mr-2 h-4 w-4" />
            {t('filters.clear')}
          </Button>
        )}
      </div>

      {clientsFilter.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="people-filter-clients-selected">
          {clientsFilter.map((client) => (
            <Badge key={client} variant="secondary" className="gap-1">
              {client}
              <button
                type="button"
                onClick={() => handleRemoveClient(client)}
                className="ml-1 hover:bg-muted rounded-full"
                data-testid={`people-filter-client-remove-${client}`}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}

      <div className="text-sm text-muted-foreground" data-testid="people-filter-count">
        {t('filters.showing', { filtered: filteredCount, total: totalCount })}
      </div>
    </div>
  );
};
